// src/components/extensions/RegexDemo.styles.ts
// Only what is specific to the hover explanation: the pattern trigger, the
// pointer, the token highlights, and the explanation card.
//
// The editor frame, file tab, code area and gutter are shared with the fold demo
// and live in editor.styles.ts.
//
// One structural constraint shapes the whole file: the code area scrolls, and a
// scroll container clips on both axes, so the card cannot be floated off the
// pattern or it would be cut off the moment the code overflowed. It is laid out
// in flow beneath the lines instead, which also means it survives a phone.
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { minDevice } from '../../styles/breakpoints';
import { monoStack, noMotion } from './editor.styles';

/* ------------------------------------------------------------- the pattern */

/**
 * The pattern, as a button, because resting the pointer on it is the extension's
 * trigger and the demo needs to offer that as something the reader can actually
 * do — including on a touch screen, where there is no hover at all.
 *
 * `font: inherit` so the button is set in the code font and size from CodeLine
 * rather than in the user-agent's button font, which would make the pattern look
 * like a button label sitting in the middle of code.
 */
export const PatternButton = styled.button`
  position: relative;
  /* inline-block rather than inline: the button holds the absolutely positioned
     pointer, and an inline formatting context is a poor containing block for
     one. vertical-align keeps the pattern on the same baseline as the code either
     side of it. */
  display: inline-block;
  vertical-align: baseline;
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  line-height: inherit;
  color: ${({ theme }) => theme.accent};
  cursor: pointer;

  &&:hover {
    text-decoration: none;
  }

  &&:focus-visible {
    outline: none;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.surface},
      0 0 0 3px ${({ theme }) => theme.accent};
  }

  ${noMotion}
`;

/**
 * One segment of the pattern.
 *
 * The active state is a solid block rather than an underline, because the whole
 * point is that the reader's eye is meant to move between the pattern above and
 * the explanation below and land on the same token in both. An underline would
 * be too easy to miss at that distance.
 *
 * Transient $active so it never reaches the DOM as an attribute.
 */
export const Token = styled.span<{ $active: boolean }>`
  border-radius: 2px;
  transition: background-color 0.15s ease, color 0.15s ease;

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.accent};
      color: ${theme.background};
    `}
`;

/** The `/` delimiters. Muted, so they never compete with a token. */
export const Delimiter = styled.span`
  color: ${({ theme }) => theme.colors.secondary};
`;

/* --------------------------------------------------------------- pointer */

/**
 * A mouse pointer resting on the pattern.
 *
 * The extension is triggered by the pointer arriving and staying put, and a card
 * that simply appears explains nothing about why. Drawn with borders rather than
 * an icon: it needs to be a pointer shape, and it is three lines of CSS instead
 * of a new dependency.
 *
 * Centred with a margin rather than `translateY(-50%)` on purpose — framer-motion
 * owns `transform` on this element for the entrance, and a CSS transform here
 * would be overwritten the moment it animated.
 */
export const Pointer = styled(motion.span)`
  position: absolute;
  left: -0.55rem;
  top: 50%;
  margin-top: -5px;
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 8px solid ${({ theme }) => theme.headingColor};
  pointer-events: none;
`;

/* ------------------------------------------------------------------ card */

/**
 * The hover card. `cardBg` rather than `surface` so it sits raised against the
 * code behind it, with a shadow to match — it is a panel floating over an
 * editor, and it should look like one.
 */
export const HoverCard = styled(motion.div)`
  margin: 0.5rem 0 0.25rem 3rem;
  overflow: hidden;
  background: ${({ theme }) => theme.cardBg};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

export const HoverHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.7rem;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  font-family: ${monoStack};
  font-size: 0.65rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.accent};
  }
`;

/** The pattern again at the top of the card, as the explainer shows it. */
export const HoverPattern = styled.div`
  padding: 0.5rem 0.7rem;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  font-family: ${monoStack};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.text};
  overflow-x: auto;
  white-space: pre;
`;

/* ------------------------------------------------------------- breakdown */

/**
 * The token-by-token breakdown, as a list so the count is announced instead of
 * being read as one run of text.
 *
 * The token column is fixed-width so every explanation starts at the same x,
 * which is what lets the eye pair a row with the pattern above it.
 */
export const Breakdown = styled.ul`
  margin: 0;
  padding: 0.35rem 0;
  list-style: none;
`;

export const BreakdownRow = styled(motion.li)<{ $active: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.15rem;
  padding: 0.3rem 0.7rem;
  border-left: 2px solid transparent;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.surface};
      border-left-color: ${theme.accent};
    `}

  @media ${minDevice.tablet} {
    grid-template-columns: 7.5rem 1fr;
    align-items: baseline;
    gap: 0.75rem;
  }
`;

/** The token itself, as a chip. Mirrors the tags on the fold demo's card. */
export const TokenChip = styled.span`
  justify-self: start;
  padding: 0.1rem 0.4rem;
  background: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: ${monoStack};
  font-size: 0.72rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.accent};
`;

/** The prose half: a small kind label, then the explanation. */
export const Explain = styled.p`
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.secondary};
`;

/**
 * What kind of token this is. Uppercase and tiny, because it is a category
 * label rather than content — but it earns its place: it is the difference
 * between "means a letter" and "a character class that means a letter", which is
 * the distinction most regex explanations blur.
 */
export const TokenKind = styled.span`
  display: block;
  margin-bottom: 0.1rem;
  font-family: ${monoStack};
  font-size: 0.58rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
  opacity: 0.6;
`;

/* ------------------------------------------------------------------ hint */

/**
 * What to do instead, while nothing is explained. Replaces the card rather than
 * sitting above it, so the panel does not change height when the explanation
 * arrives.
 */
export const HoverHint = styled(motion.p)`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.5rem 0 0.25rem 3rem;
  font-family: ${monoStack};
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.secondary};

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.accent};
  }
`;
